import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  DashboardSummary,
  JobsCreatedReport,
  ConversionFunnel,
  LeadSourceReport,
  UserComparisonReport,
  UserPerformance,
} from '../../../types/reporting';
import { reportingService } from '../../../services/reportingService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface ReportExportDialogProps {
  open: boolean;
  onClose: () => void;
  users: UserPerformance[];
  selectedUserIds: string[];
  onSelectionChange: (userIds: string[]) => void;
  dashboardSummary?: DashboardSummary;
  jobsCreated?: JobsCreatedReport;
  conversionFunnel?: ConversionFunnel;
  leadSources?: LeadSourceReport[];
  userComparison?: UserComparisonReport;
  period: string;
}

const ReportExportDialog: React.FC<ReportExportDialogProps> = ({
  open,
  onClose,
  users,
  selectedUserIds,
  onSelectionChange,
  dashboardSummary,
  jobsCreated,
  conversionFunnel,
  leadSources,
  userComparison,
  period,
}) => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const handleSelectAll = () => {
    if (selectedUserIds.length === users.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(users.map((u) => u.userId));
    }
  };

  const handleUserToggle = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onSelectionChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelectionChange([...selectedUserIds, userId]);
    }
  };

  const generatePDF = async () => {
    setExporting(true);
    setError(null);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Header
      pdf.setFontSize(20);
      pdf.setTextColor(33, 37, 41);
      pdf.text('Reporting Dashboard', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(10);
      pdf.setTextColor(108, 117, 125);
      pdf.text(`Period: ${period}`, margin, yPosition);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - margin - 40, yPosition);
      yPosition += 10;

      // Divider
      pdf.setDrawColor(200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      // KPIs
      if (dashboardSummary) {
        pdf.setFontSize(14);
        pdf.setTextColor(33, 37, 41);
        pdf.text('Key Performance Indicators', margin, yPosition);
        yPosition += 8;

        const kpis = [
          {
            label: 'Total Jobs Created',
            value: dashboardSummary.totalJobsCreated.toString(),
            change: dashboardSummary.jobsCreatedChange,
          },
          {
            label: 'Conversion Rate',
            value: `${dashboardSummary.conversionRate.toFixed(1)}%`,
            change: dashboardSummary.conversionRateChange,
          },
          {
            label: 'Total Revenue',
            value: reportingService.formatCurrency(dashboardSummary.totalRevenue),
            change: dashboardSummary.revenueChange,
          },
          {
            label: 'Average Job Value',
            value: reportingService.formatCurrency(dashboardSummary.averageJobValue),
            change: dashboardSummary.averageJobValueChange,
          },
        ];

        pdf.setFontSize(10);
        const kpiWidth = (pageWidth - margin * 2 - 30) / 4;
        kpis.forEach((kpi, index) => {
          const xPos = margin + index * (kpiWidth + 10);
          pdf.setTextColor(108, 117, 125);
          pdf.text(kpi.label, xPos, yPosition);
          pdf.setTextColor(33, 37, 41);
          pdf.setFontSize(12);
          pdf.text(kpi.value, xPos, yPosition + 6);
          pdf.setFontSize(9);
          pdf.setTextColor(kpi.change >= 0 ? 40 : 220, kpi.change >= 0 ? 167 : 53, kpi.change >= 0 ? 69 : 69);
          pdf.text(`${kpi.change >= 0 ? '+' : ''}${kpi.change.toFixed(1)}%`, xPos, yPosition + 11);
          pdf.setFontSize(10);
        });
        yPosition += 25;
      }

      // Conversion Funnel Summary
      if (conversionFunnel) {
        pdf.setFontSize(14);
        pdf.setTextColor(33, 37, 41);
        pdf.text('Conversion Funnel', margin, yPosition);
        yPosition += 8;

        pdf.setFontSize(10);
        pdf.setTextColor(108, 117, 125);
        pdf.text(`Total Leads: ${conversionFunnel.totalLeads}`, margin, yPosition);
        pdf.text(`Converted: ${conversionFunnel.totalConverted}`, margin + 50, yPosition);
        pdf.text(`Lost: ${conversionFunnel.totalLost}`, margin + 100, yPosition);
        pdf.text(`Overall Conversion: ${conversionFunnel.overallConversionRate.toFixed(1)}%`, margin + 140, yPosition);
        yPosition += 15;
      }

      // User Performance Table
      if (userComparison && selectedUserIds.length > 0) {
        pdf.setFontSize(14);
        pdf.setTextColor(33, 37, 41);
        pdf.text('User Performance', margin, yPosition);
        yPosition += 8;

        const filteredUsers = userComparison.users.filter((u) =>
          selectedUserIds.includes(u.userId)
        );

        // Table headers
        const columns = ['User', 'Appointments', 'Jobs', 'Converted', 'Conv %', 'Revenue', 'Avg Value'];
        const columnWidths = [40, 25, 20, 25, 20, 30, 30];

        pdf.setFontSize(9);
        pdf.setTextColor(255);
        pdf.setFillColor(66, 66, 66);
        pdf.rect(margin, yPosition - 4, pageWidth - margin * 2, 8, 'F');

        let xPos = margin + 2;
        columns.forEach((col, index) => {
          pdf.text(col, xPos, yPosition);
          xPos += columnWidths[index];
        });
        yPosition += 6;

        // Table rows
        pdf.setTextColor(33, 37, 41);
        filteredUsers.forEach((user, rowIndex) => {
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = margin;
          }

          if (rowIndex % 2 === 0) {
            pdf.setFillColor(245, 245, 245);
            pdf.rect(margin, yPosition - 4, pageWidth - margin * 2, 7, 'F');
          }

          xPos = margin + 2;
          const rowData = [
            user.userName.substring(0, 20),
            user.appointmentsCount.toString(),
            user.jobsCreated.toString(),
            user.jobsConverted.toString(),
            `${user.conversionRate.toFixed(1)}%`,
            reportingService.formatCurrency(user.totalRevenue),
            reportingService.formatCurrency(user.averageJobValue),
          ];

          rowData.forEach((cell, index) => {
            pdf.text(cell, xPos, yPosition);
            xPos += columnWidths[index];
          });
          yPosition += 7;
        });

        // Totals row
        if (userComparison.totals) {
          pdf.setFillColor(230, 230, 230);
          pdf.rect(margin, yPosition - 4, pageWidth - margin * 2, 7, 'F');
          pdf.setFontSize(9);

          xPos = margin + 2;
          const totalsData = [
            'Total',
            userComparison.totals.appointmentsCount.toString(),
            userComparison.totals.jobsCreated.toString(),
            userComparison.totals.jobsConverted.toString(),
            `${userComparison.totals.conversionRate.toFixed(1)}%`,
            reportingService.formatCurrency(userComparison.totals.totalRevenue),
            reportingService.formatCurrency(userComparison.totals.averageJobValue),
          ];

          totalsData.forEach((cell, index) => {
            pdf.text(cell, xPos, yPosition);
            xPos += columnWidths[index];
          });
        }
      }

      // Save PDF
      const filename = `reporting-dashboard-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      onClose();
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export Report to PDF</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which users to include in the PDF report:
        </Typography>

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedUserIds.length === users.length}
                indeterminate={
                  selectedUserIds.length > 0 && selectedUserIds.length < users.length
                }
                onChange={handleSelectAll}
              />
            }
            label={<Typography fontWeight={500}>Select All Users</Typography>}
          />
          <Divider sx={{ my: 1 }} />
          <Box sx={{ maxHeight: 300, overflowY: 'auto', pl: 2 }}>
            {users.map((user) => (
              <FormControlLabel
                key={user.userId}
                control={
                  <Checkbox
                    checked={selectedUserIds.includes(user.userId)}
                    onChange={() => handleUserToggle(user.userId)}
                  />
                }
                label={user.userName}
              />
            ))}
          </Box>
        </FormGroup>

        {users.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No users available
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={generatePDF}
          disabled={exporting || selectedUserIds.length === 0}
          startIcon={exporting ? <CircularProgress size={16} /> : null}
        >
          {exporting ? 'Generating...' : 'Export PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportExportDialog;
